const DEFAULT_WIDGET_BRAND_COLOR = '#2E9B4A'
const BRAND_MIX_RATIO = 0.12
const DISABLED_LIGHTNESS_MIX_RATIO = 0.5625
const DISABLED_CHROMA_RATIO = 0.5388
const DISABLED_HUE_SHIFT_RADIANS = (-2.3 * Math.PI) / 180
const CHROMA_EPSILON = 1e-7

type RgbColor = [red: number, green: number, blue: number]
type OklabColor = [lightness: number, greenRed: number, blueYellow: number]

export interface WidgetBrandCssVariables {
  '--abot-brand-color': string
  '--abot-brand-surface': string
  '--abot-brand-border': string
  '--abot-brand-disabled': string
}

export interface WidgetBrandStyle {
  color: string
  surfaceColor: string
  borderColor: string
  disabledColor: string
  cssVariables: WidgetBrandCssVariables
}

const expandHexColor = (value: string): string =>
  value.length === 4 ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}` : value

const normalizeHexColor = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_WIDGET_BRAND_COLOR

  const trimmedValue = value.trim()
  if (!/^#[\da-f]{3}([\da-f]{3})?$/i.test(trimmedValue)) {
    return DEFAULT_WIDGET_BRAND_COLOR
  }

  return expandHexColor(trimmedValue).toUpperCase()
}

const mixChannelWithWhite = (channel: number): number =>
  Math.round(channel * BRAND_MIX_RATIO + 255 * (1 - BRAND_MIX_RATIO))

const getMixedRgb = (color: string): [number, number, number] => [
  mixChannelWithWhite(Number.parseInt(color.slice(1, 3), 16)),
  mixChannelWithWhite(Number.parseInt(color.slice(3, 5), 16)),
  mixChannelWithWhite(Number.parseInt(color.slice(5, 7), 16)),
]

const convertSrgbChannelToLinear = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4

const convertLinearChannelToSrgb = (channel: number): number =>
  channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055

const getRgbFromHex = (color: string): RgbColor => [
  Number.parseInt(color.slice(1, 3), 16) / 255,
  Number.parseInt(color.slice(3, 5), 16) / 255,
  Number.parseInt(color.slice(5, 7), 16) / 255,
]

const convertRgbToOklab = ([red, green, blue]: RgbColor): OklabColor => {
  const linearRed = convertSrgbChannelToLinear(red)
  const linearGreen = convertSrgbChannelToLinear(green)
  const linearBlue = convertSrgbChannelToLinear(blue)
  const longWave = Math.cbrt(
    0.4122214708 * linearRed + 0.5363325363 * linearGreen + 0.0514459929 * linearBlue,
  )
  const mediumWave = Math.cbrt(
    0.2119034982 * linearRed + 0.6806995451 * linearGreen + 0.1073969566 * linearBlue,
  )
  const shortWave = Math.cbrt(
    0.0883024619 * linearRed + 0.2817188376 * linearGreen + 0.6299787005 * linearBlue,
  )

  return [
    0.2104542553 * longWave + 0.793617785 * mediumWave - 0.0040720468 * shortWave,
    1.9779984951 * longWave - 2.428592205 * mediumWave + 0.4505937099 * shortWave,
    0.0259040371 * longWave + 0.7827717662 * mediumWave - 0.808675766 * shortWave,
  ]
}

const getHexChannel = (linearChannel: number): string => {
  const channel = Math.round(convertLinearChannelToSrgb(linearChannel) * 255)
  return Math.min(255, Math.max(0, channel)).toString(16).padStart(2, '0').toUpperCase()
}

const convertOklabToHex = ([lightness, greenRed, blueYellow]: OklabColor): string => {
  const longWave = lightness + 0.3963377774 * greenRed + 0.2158037573 * blueYellow
  const mediumWave = lightness - 0.1055613458 * greenRed - 0.0638541728 * blueYellow
  const shortWave = lightness - 0.0894841775 * greenRed - 1.291485548 * blueYellow
  const longWaveCube = longWave ** 3
  const mediumWaveCube = mediumWave ** 3
  const shortWaveCube = shortWave ** 3

  const linearRed =
    4.0767416621 * longWaveCube - 3.3077115913 * mediumWaveCube + 0.2309699292 * shortWaveCube
  const linearGreen =
    -1.2684380046 * longWaveCube + 2.6097574011 * mediumWaveCube - 0.3413193965 * shortWaveCube
  const linearBlue =
    -0.0041960863 * longWaveCube - 0.7034186147 * mediumWaveCube + 1.707614701 * shortWaveCube

  return `#${getHexChannel(linearRed)}${getHexChannel(linearGreen)}${getHexChannel(linearBlue)}`
}

/**
 * Produces a lighter, less chromatic fill while preserving the configured brand hue. The
 * perceptual ratios map the default #2E9B4A brand to the approved #A5D6A7 disabled state.
 */
const getDisabledBrandColor = (color: string): string => {
  const [lightness, greenRed, blueYellow] = convertRgbToOklab(getRgbFromHex(color))
  const chroma = Math.hypot(greenRed, blueYellow)
  const disabledLightness = lightness + (1 - lightness) * DISABLED_LIGHTNESS_MIX_RATIO

  if (chroma < CHROMA_EPSILON) {
    return convertOklabToHex([disabledLightness, 0, 0])
  }

  const disabledChroma = chroma * DISABLED_CHROMA_RATIO
  const disabledHue = Math.atan2(blueYellow, greenRed) + DISABLED_HUE_SHIFT_RADIANS

  return convertOklabToHex([
    disabledLightness,
    disabledChroma * Math.cos(disabledHue),
    disabledChroma * Math.sin(disabledHue),
  ])
}

/**
 * Converts an untrusted Widget brand color into the opaque accent, translucent surfaces, and
 * perceptually related disabled fill used by the floating dialog.
 */
export const normalizeWidgetBrandStyle = (boxColor?: unknown): WidgetBrandStyle => {
  const color = normalizeHexColor(boxColor)
  const [red, green, blue] = getMixedRgb(color)
  const surfaceColor = `rgba(${red}, ${green}, ${blue}, 0.8)`
  const borderColor = `rgba(${red}, ${green}, ${blue}, 0.5)`
  const disabledColor = getDisabledBrandColor(color)

  return {
    color,
    surfaceColor,
    borderColor,
    disabledColor,
    cssVariables: {
      '--abot-brand-color': color,
      '--abot-brand-surface': surfaceColor,
      '--abot-brand-border': borderColor,
      '--abot-brand-disabled': disabledColor,
    },
  }
}
